import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { NewColorStateDialogComponent } from '@components/new-color-state-dialog/new-color-state-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { SystemColorSocketService } from '@service/system-color-socket.service';
import { Channel } from 'icreate/common/models/channel';
import { RangeSetting } from 'icreate/common/models/editor-viewer-element';
import { SystemColor, SystemColorType } from 'icreate/common/models/system-color';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-range-indicators',
    templateUrl: './range-indicators.component.html',
    styleUrls: ['./range-indicators.component.css'],
    standalone: false,
})
export class RangeIndicatorsComponent implements OnChanges, OnInit, OnDestroy {
    @Input() assignedChannel: Channel;
    @Input() foreignObject: SVGElement;
    @Input() rangesArray: RangeSetting[];
    @Input() userCanEdit: boolean;
    @Input() systemColors: SystemColor[];

    colorList: Array<RangeSetting & { color: string }> = [];
    customColorList: SystemColor[];
    vesselId: string;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private systemColorSocketService: SystemColorSocketService
    ) {}

    async ngOnInit() {
        this.vesselId = this.route.snapshot.paramMap.get('vesselId');
        this.customColorList = this.systemColors.filter(color => color.type == SystemColorType.Custom);

        this.colorList = this.rangesArray.map(range => {
            let selectedSystemColor = this.customColorList.find(systemColor => systemColor.id === range.colorId);
            return {
                ...range,
                color: selectedSystemColor?.color,
            };
        });

        this.systemColorSocketService
            .systemColorCreated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(newSystemColor => {
                if (newSystemColor.vesselId === this.vesselId) {
                    if (newSystemColor.type === SystemColorType.Custom) {
                        this.customColorList = [...this.customColorList, newSystemColor];
                    }
                }
            });

        this.systemColorSocketService
            .systemColorUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async updatedSystemColor => {
                if (updatedSystemColor.vesselId === this.vesselId) {
                    if (updatedSystemColor.type === SystemColorType.Custom) {
                        this.customColorList = this.customColorList.map(color => {
                            if (updatedSystemColor.id === color.id) {
                                return updatedSystemColor;
                            } else {
                                return color;
                            }
                        });
                        this.colorList = this.rangesArray.map((range, index) => {
                            if (range.colorId === updatedSystemColor.id) {
                                return {
                                    ...range,
                                    color: updatedSystemColor.color,
                                };
                            } else {
                                return this.colorList[index];
                            }
                        });
                    }
                }
            });
    }

    ngOnChanges(): void {
        if (!this.rangesArray) {
            this.rangesArray = [];
            this.colorList = [];
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async createRange() {
        if (!this.userCanEdit) return;
        const dialogRef = this.dialog.open(NewColorStateDialogComponent, {
            width: '474px',
            autoFocus: false,
            data: {
                rangesArray: this.rangesArray,
                vesselId: this.vesselId,
            },
        });

        const newRange = await firstValueFrom(dialogRef.afterClosed());

        if (newRange) {
            this.rangesArray.push(newRange);
            this.setRangeColor();
        }
    }

    handleRangeChanged(updatedRange, index) {
        if (this.rangesArray[index]) {
            this.rangesArray[index] = updatedRange;
        }

        this.setRangeColor();
    }

    setRangeColor() {
        this.colorList = this.rangesArray.map(range => {
            let selectedSystemColor = this.customColorList.find(systemColor => systemColor.id === range.colorId);
            return {
                ...range,
                color: selectedSystemColor?.color,
            };
        });
    }

    deleteRange(index) {
        if (!this.userCanEdit) return;
        this.rangesArray = [
            ...this.rangesArray.slice(0, index),
            ...this.rangesArray.slice(index + 1, this.rangesArray.length),
        ];

        this.setRangeColor();
    }

    getRanges(): RangeSetting[] {
        return this.rangesArray;
    }

    getRangesErrorMessage(range): string {
        let errorMessage = '';

        const min = this.assignedChannel?.fcuData?.measurableRange.min;
        const max = this.assignedChannel?.fcuData?.measurableRange.max;

        for (let i = 0; i < range.length; i++) {
            const current = range[i];
            if (current.min >= current.max) {
                const message = this.translateService.instant('mimic.settings.range.error.min-max', {
                    min: current.min,
                    max: current.max,
                    index: i + 1,
                });
                errorMessage += `${message}\n`;
            }
            if (current.min < min || current.max > max) {
                const message = this.translateService.instant('mimic.settings.range.error.exceed-range', {
                    min: current.min,
                    max: current.max,
                    index: i + 1,
                });
                errorMessage += `${message}\n`;
            }
            if (i > 0) {
                const prev = range[i - 1];
                if (current.min <= prev.max) {
                    const message = this.translateService.instant('mimic.settings.range.error.previous-max', {
                        min: current.min,
                        prevMax: prev.max,
                        index: i + 1,
                    });
                    errorMessage += `${message}\n`;
                }
            }
            if (i < range.length - 1) {
                const next = range[i + 1];
                if (current.max >= next.min) {
                    const message = this.translateService.instant('mimic.settings.range.error.next-min', {
                        nextMin: next.min,
                        max: current.max,
                        index: i + 1,
                    });
                    errorMessage += `${message}\n`;
                }
            }
        }

        return errorMessage;
    }
}
